import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Generator.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Slot type colors
const TYPE_COLORS = {
  opener: '#22c55e',
  jog: '#3b82f6', 
  climb: '#f97316',
  heavy_climb: '#ef4444',
  flat: '#8b5cf6',
  slow: '#ec4899',
  sprint: '#eab308',
  arms: '#06b6d4',
  closer: '#22c55e'
};

function Generator() {
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [createdPlaylist, setCreatedPlaylist] = useState(null);
  const [generationStats, setGenerationStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/generator/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const generatePlaylist = async () => {
    setLoading(true);
    setError(null);
    setCreatedPlaylist(null);
    
    try {
      const response = await axios.post(
        `${API_BASE}/generator/generate`,
        {},
        { withCredentials: true }
      );
      setPlaylist(response.data.playlist);
      setGenerationStats(response.data.stats);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate playlist');
    } finally {
      setLoading(false);
    }
  };

  const createSpotifyPlaylist = async () => {
    if (!playlist) return;
    
    setCreating(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_BASE}/generator/create-spotify`,
        { playlist },
        { withCredentials: true }
      );
      setCreatedPlaylist(response.data.playlist);
    } catch (err) {
      if (err.response?.data?.authUrl) {
        setError('Please authorize Spotify first: ' + err.response.data.authUrl);
      } else {
        setError(err.response?.data?.error || 'Failed to create Spotify playlist');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="generator">
      <div className="generator-header">
        <h1>🎵 Smart Playlist Generator</h1>
        {stats && (
          <div className="stats">
            <span className="stat">
              <strong>{stats.eligibleSongs}</strong> eligible songs
            </span>
            <span className="stat-divider">•</span>
            <span className="stat">
              Not used in {stats.sixMonthWindow}
            </span>
            {stats.curatedRoles && (
              <>
                <span className="stat-divider">•</span>
                <span className="stat">
                  <strong>{Object.values(stats.curatedRoles).reduce((a,b) => a+b, 0)}</strong> curated tags
                </span>
              </>
            )}
          </div>
        )}
        <div className="generator-nav">
          <Link to="/library" className="generator-nav-link">
            📚 Browse Library
          </Link>
        </div>
      </div>

      <div className="generator-actions">
        <button 
          className="btn-generate" 
          onClick={generatePlaylist}
          disabled={loading}
        >
          {loading ? '⏳ Generating...' : '🎲 Generate New Playlist'}
        </button>
        
        {playlist && !createdPlaylist && (
          <button 
            className="btn-create" 
            onClick={createSpotifyPlaylist}
            disabled={creating}
          >
            {creating ? '⏳ Creating...' : '✨ Create in Spotify'}
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {createdPlaylist && (
        <div className="success-message">
          <h3>✅ Playlist Created!</h3>
          <p><strong>{createdPlaylist.name}</strong></p>
          <p>{createdPlaylist.trackCount} tracks added</p>
          <a 
            href={createdPlaylist.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="spotify-link"
          >
            Open in Spotify →
          </a>
        </div>
      )}

      {playlist && generationStats && (
        <div className="generation-summary">
          <div className="summary-stat">
            <span className="summary-label">Curated</span>
            <span className="summary-value">{generationStats.curatedSelections}/{generationStats.songCount}</span>
          </div>
          <div className="summary-stat">
            <span className="summary-label">BPM Range</span>
            <span className="summary-value">{generationStats.bpmRange?.min}-{generationStats.bpmRange?.max}</span>
          </div>
          <div className="summary-stat">
            <span className="summary-label">Duration</span>
            <span className="summary-value">~{generationStats.estimatedMinutes} min</span>
          </div>
        </div>
      )}

      {playlist && (
        <div className="playlist-preview">
          <h2>Generated Playlist</h2>
          
          <div className="playlist-tracks">
            {playlist.map((item, index) => (
              <div key={index} className={`track-row ${item.isCurated ? 'curated' : ''}`}>
                <div className="track-position">{item.position}</div>
                <div 
                  className="track-type"
                  style={{ backgroundColor: TYPE_COLORS[item.type] || '#666' }}
                >
                  {item.type.replace('_', ' ')}
                </div>
                <div className="track-info">
                  <div className="track-name">
                    <span className="track-name-text">{item.song.name}</span>
                    {item.isCurated && <span className="curated-badge" title="From curated collection">⭐</span>}
                  </div>
                  <div className="track-artist">
                    {item.song.artists.join(', ')}
                  </div>
                </div>
                <div className="track-bpm">
                  <span className="bpm-value">{item.song.bpm}</span>
                  <span className="bpm-label">BPM</span>
                </div>
                <div className="track-target">
                  <div>Target: {item.targetBpm}</div>
                  {item.song.last_played && (
                    <div className="track-last-played">
                      Last: {new Date(item.song.last_played).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="generator-rules">
        <h3>Generation Rules</h3>
        <ul>
          <li>✅ No songs used in last 6 months</li>
          <li>✅ No artist repeats</li>
          <li>✅ BPM matched to ride type with arc logic</li>
          <li>⭐ Curated collections: openers, closers, slow songs, power pushes</li>
          <li>✅ Smart scoring: curated songs preferred for key slots</li>
          <li>✅ 14-song structure: opener → climbs → slow → sprint → closer</li>
        </ul>
      </div>
    </div>
  );
}

export default Generator;
